import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import LoadingScreen from "../components/LoadingScreen"
import useFormUtility from "../utilities/useFormUtility";
import Table from "../components/Table";
import RuleSection from "../sections/RuleSection";
import MenuPage from "../components/MenuPage";
import useHttpUtility from "../utilities/useHttpUtility";
import useRateUtility from "../utilities/useRateUtility";
import OverrideSection from "../sections/OverrideSection";
import MenuPageItemDiscard from "../components/MenuPageItemDiscard";
import MenuPageItemCopy from "../components/MenuPageItemCopy";
import MenuPageItemSave from "../components/MenuPageItemSave";
import MenuPageItemDelete from "../components/MenuPageItemDelete";
import FormFieldText from "../components/FormFieldText";
import FormFieldNumber from "../components/FormFieldNumber";
import FormFieldCheckbox from "../components/FormFieldCheckbox";
import FormFieldSelect from "../components/FormFieldSelect";

export default function PageRate() {

    const navigate = useNavigate();
    const http = useHttpUtility();
    const rateUtility = useRateUtility();

    const { id } = useParams();

    const formUtility = useFormUtility(null);

    const [selectedIndexRule, setSelectedIndexRule] = useState(-1);
    const [selectedIndexOverride, setSelectedIndexOverride] = useState(-1);

    useEffect(() => {
        if (!id || id === "0") {
            formUtility.setData({
                "id": null,
                "rateType": "1",
                "active": false,
                "phoneRequired": true,
                "rules": [],
                "rateMode": "1",
                "rate": "",
                "baseRate": "",
                "overrides": []
            }, true);
        }
        else {
            http.get(`/api/Rates/${id}`, (d) => formUtility.setData({ ...d, rateType: d.rateType.toString(), rateMode: d.rateMode.toString() }));
        }
    }, []);

    function handleOnOkRule(rule) {
        if (selectedIndexRule < 0 && selectedIndexRule !== -2)
            return;

        let rules = [...formUtility.data.rules];

        selectedIndexRule >= 0
            ? rules.splice(selectedIndexRule, 1, rule)
            : rules.push(rule);

        formUtility.handleChange([{ field: "rules", value: rules }]);
        setSelectedIndexRule(-1);
    }

    function handleOnRemoveRule(idx) {
        if (idx < 0)
            return;

        let rules = [...formUtility.data.rules];
        rules.splice(idx, 1);
        formUtility.handleChange([{ field: "rules", value: rules }]);
    }

    function handleOnOkOverride(override) {
        if (selectedIndexOverride < 0 && selectedIndexOverride !== -2)
            return;

        let overrides = [...formUtility.data.overrides];

        selectedIndexOverride >= 0
            ? overrides.splice(selectedIndexOverride, 1, override)
            : overrides.push(override);

        formUtility.handleChange([{ field: "overrides", value: overrides }]);
        setSelectedIndexOverride(-1);
    }

    function handleOnRemoveOverride(idx) {
        if (idx < 0)
            return;

        let overrides = [...formUtility.data.overrides];
        overrides.splice(idx, 1);
        formUtility.handleChange([{ field: "overrides", value: overrides }]);
    }

    async function handleSave() {
        let rate = (formUtility.data.rate ?? "").toString().trim() === "" ? 0 : formUtility.data.rate;
        let baseRate = formUtility.data.rateMode === "1" || (formUtility.data.baseRate ?? "").toString().trim() === "" ? 0 : formUtility.data.baseRate;

        var numberRate = Number(rate);
        if (isNaN(numberRate) || numberRate < 0)
            return formUtility.showNotification(2, "Invalid rate.");

        var numberBaseRate = Number(baseRate);
        if (isNaN(numberBaseRate) || numberBaseRate < 0)
            return formUtility.showNotification(2, "Invalid base rate.");

        let data = { ...formUtility.data, rate: numberRate, baseRate: numberBaseRate };
        
        await http.post(
            `/api/Rates/${data.id ? "Update" : "Create"}`,
            data,
            (d) => formUtility.setData({ ...d, rateType: d.rateType.toString(), rateMode: d.rateMode.toString()})
        );
    }

    return (
        !formUtility.data
            ? <LoadingScreen />
            :
            <div className="page page-rate">
                {
                    selectedIndexRule >= 0 || selectedIndexRule === -2 ?
                        <RuleSection data={selectedIndexRule === -2 ? null : formUtility.data.rules[selectedIndexRule]}
                            onDiscard={() => setSelectedIndexRule(-1)}
                            onOk={handleOnOkRule} />
                        : selectedIndexOverride >= 0 || selectedIndexOverride === -2 ?
                            <OverrideSection
                                data={selectedIndexOverride === -2 ? null : formUtility.data.overrides[selectedIndexOverride]}
                                sortOrder={selectedIndexOverride === -2 ? formUtility.data.overrides.map(o => o.sortOrder).reduce((p, c) => c > p ? c : p, 0) + 1 : null}
                                onDiscard={() => setSelectedIndexOverride(-1)}
                                onOk={handleOnOkOverride} />
                        :
                        <>
                            <MenuPage>
                                <MenuPageItemDiscard formUtility={formUtility} onClick={() => navigate(`..${window.location.search}`)} />
                                <MenuPageItemCopy
                                    formUtility={formUtility}
                                    onClick={() => {
                                        var newData = {
                                            ...formUtility.data,
                                            id: null,
                                            name: "",
                                            rules: formUtility.data.rules.map(r => ({
                                                ...r,
                                                id: null
                                            })),
                                            overrides: formUtility.data.overrides.map(o => ({
                                                ...o,
                                                id: null,
                                                rules: o.rules.map(r => ({
                                                    ...r,
                                                    id: null
                                                }))
                                            }))
                                        };
                                        formUtility.setData(newData, true);
                                    }
                                } />
                                <MenuPageItemSave formUtility={formUtility} onClick={handleSave} />
                                <MenuPageItemDelete formUtility={formUtility} onClick={() => http.delete(`/api/Rates/${formUtility.data.id}`, () => navigate(`..${window.location.search}`))} />
                            </MenuPage>
                            <FormFieldSelect
                                formUtility={formUtility}
                                field="rateType"
                                label="Rate Type"
                                disabled={formUtility.data.id || formUtility.data.rules.length > 0 || formUtility.data.overrides.length > 0}
                                options={rateUtility.rateTypes}
                                description={formUtility.data.rateType === "1"
                                    ? "A rate which is shown to the user for selection at checkout based on delivery windows.  This rate can be assigned to one or many delivery windows."
                                    : "A rate which is shown to the user for selection at checkout.  This rate type must be matched with a post checkout windows group to determine which orders will trigger a delivery booking."
                                } />
                            <FormFieldText formUtility={formUtility} field="name" label="Name" max="30" description={formUtility.data.rateType === "1" ? "The rate name for internal use." : formUtility.data.rateType === "2" ?  "The rate name shown to the customer." : null} />
                            <FormFieldText formUtility={formUtility} field="description" label="Description (Optional)" max="250" />
                            <br/>
                            <h2>Rate</h2>
                                <div className="page-section row">
                                    <FormFieldSelect
                                        formUtility={formUtility}
                                        field="rateMode"
                                        label="Rate Mode"
                                        options={rateUtility.rateModes}
                                        description={
                                            formUtility.data.rateMode === "1" ? "Allows you to set a specific rate you want to charge for shipping."
                                                : formUtility.data.rateMode === "2" ? "Allows you charge a percentage of the fulfillment value for shipping."
                                                    : formUtility.data.rateMode === "3" ? "Allows you charge a rate per item in the fulfillment for shipping."
                                                        : "Allows you charge a rate per kilogram in the fulfillment for shipping."
                                        }
                                        className={`form-group col-md-${formUtility.data.rateMode === "1" ? "6" : "4"}`} />
                                    <FormFieldNumber formUtility={formUtility}
                                        field="rate"
                                        label="Rate"
                                        description={formUtility.data.rateMode === "1" ? "The rate for shipping." : "The rate for calculations."}
                                        className={`form-group col-md-${formUtility.data.rateMode === "1" ? "6" : "4"}`}
                                    />
                                    {
                                        formUtility.data.rateMode !== "1"
                                            ?
                                            <FormFieldNumber formUtility={formUtility}
                                                field="baseRate"
                                                label="Base Rate"
                                                description="The base rate added to the calculated rate."
                                                className="form-group col-md-4"
                                            />
                                            : null
                                    }
                                </div>
                                {
                                    formUtility.data.rateType === "2"
                                        ?
                                        <>
                                            <FormFieldCheckbox formUtility={formUtility} field="active" label="Active" description="Allows you to enable or disable the rate as required." />
                                            <FormFieldCheckbox formUtility={formUtility} field="phoneRequired" label="Phone Required" description="When checked the rate will require the user to input a phone number." />
                                            <br />
                                            <h2>Rules</h2>
                                            <p>Allows you to set up user defined rules that tell the cart when to offer the rate, or when not to offer the rate.</p>
                                            <Table
                                                data={formUtility.data.rules}
                                                mappings={
                                                    [
                                                        {
                                                            header: "Type",
                                                            field: "ruleType",
                                                            dataType: "ruleType"
                                                        },
                                                        {
                                                            header: "Condition",
                                                            field: "condition",
                                                            dataType: "condition"
                                                        },
                                                        {
                                                            header: "Value",
                                                            field: "value"
                                                        },
                                                        {
                                                            header: "Or Group",
                                                            field: "orGroup"
                                                        }
                                                    ]
                                                }
                                                onClickAdd={() => setSelectedIndexRule(-2)}
                                                onClick={setSelectedIndexRule}
                                                onRemove={handleOnRemoveRule}
                                            />
                                        </>
                                        : null
                                }
                                <br />
                                <h2>Overrides</h2>
                                <p>Allows you to override the rate based on defined rules.  If overrides are provided, the rate will use the first valid override it finds based on sort order.</p>
                                <Table
                                    formUtility={formUtility}
                                    data={
                                        formUtility.data.overrides.map(x => ({
                                            ...x,
                                            rateDisplay: `$${x.rate}${x.rateMode !== 1 && x.baseRate !== 0 ? ` ($${x.baseRate})` : ""}`
                                        }))
                                    }
                                    mappings={
                                        [
                                            {
                                                header: "Mode",
                                                field: "rateMode",
                                                dataType: "rateMode"
                                            },
                                            {
                                                header: "Rate",
                                                field: "rateDisplay"
                                            },
                                            {
                                                header: "Sort Order",
                                                field: "sortOrder"
                                            }
                                        ]
                                    }
                                    onClickAdd={() => setSelectedIndexOverride(-2)}
                                    onClick={setSelectedIndexOverride}
                                    onRemove={handleOnRemoveOverride}
                                />
                        </>
                }
            </div>
    );
}